import * as React from 'react';
import { cn } from '../../utils';
import { Dropbox } from '../Dropbox/dropbox';
import './typeahead.scss';
import { SearchInput } from '../SearchInput/searchInput';
import { SROpts } from '../../types/SROpts';

type Props<T = any> = Pick<
  React.HTMLAttributes<HTMLInputElement>,
  'className' | 'placeholder' | 'onFocus' | 'onBlur' | 'children'
> & {
  value?: string;
  noMatchMessage?: string;
  inputContainer?: any;
  inline?: boolean;
  disabled?: boolean;
  options: T[];
  includes?: (txt: string, o: T) => boolean;
  showAllByDefault?: boolean;
  sort?: (a: T, b: T) => number;
  renderOption?: (o: T) => any;
  onSelect?: (o: T, opts?: SROpts) => void;
  onTextChange?: (t: string) => void;
  onRawTextChange?: (t: string) => void;
  renderSearchResult?: (o: T[], opts?: SROpts) => any;
};

type P<T = any> = Partial<Props<T>> & {
  matched?: T[];
  handleSearch?: (t: string) => void;
  setExpanded?: (e: boolean) => void;
  setText?: (t: string) => void;
};

const defaultIncludes = (t: string, b: any) => {
  if (typeof b == 'string') {
    return b.toLowerCase().includes(t?.trim().toLowerCase());
  } else {
    const s = b?.displayName || b?.label || b?.name || b?.description;
    if (s) return s.toLowerCase().includes(t?.trim().toLowerCase());
  }
  return true;
};

const defaultRenderOption = o => {
  if (typeof o == 'string') return o;
  if (typeof o?.label == 'string') return o.label;
  if (typeof o?.name == 'string') return o.name;
  return o;
};

export const Typeahead = <T,>({
  className,
  inline,
  value,
  options,
  includes = defaultIncludes,
  renderOption = defaultRenderOption,
  onSelect,
  onTextChange,
  showAllByDefault,
  renderSearchResult,
  children,
  noMatchMessage,
  sort,
  ...p
}: Props<T>) => {
  const [matched, _setMatched] = React.useState(
    showAllByDefault ? options : []
  );
  const setMatched = (ts: T[]) => {
    if (sort) _setMatched(ts.sort(sort));
    else _setMatched(ts);
  };

  const [text, setText] = React.useState<string>(value || '');

  const handleSearch = (t: string) => {
    setText(t);
    if (onTextChange) onTextChange(t);
    if (!t && showAllByDefault) {
      setMatched(options);
    } else {
      setMatched(options.filter(o => includes(t, o)));
    }
  };
  React.useEffect(() => {
    handleSearch(text);
  }, [options]);

  const _onSelect = (o: T) => {
    onSelect(o, { setText: handleSearch });
  };

  const searchResultProps = {
    matched,
    children,
    setText,
    value: text,
    renderOption,
    onSelect: _onSelect,
    noMatchMessage,
    renderSearchResult
  };
  React.useEffect(() => {
    if (value != text) handleSearch(value || '');
  }, [value]);

  return (
    <div
      className={cn(
        {
          'typeahead-inline': inline,
          [className]: !!className
        },
        'typeahead'
      )}
    >
      {inline ? (
        <>
          <InputGroup {...p} handleSearch={handleSearch} value={text}/>
          <div>
            <SearchResult {...searchResultProps} />
          </div>
        </>
      ) : (
        <Dropbox
          disabled={p.disabled}
          renderBox={s => (
            <SearchResult {...searchResultProps} setExpanded={s} />
          )}
        >
          {setExpanded => (
            <InputGroup
              {...p}
              handleSearch={t => {
                setExpanded(true);
                handleSearch(t);
              }}
              value={text}
            />
          )}
        </Dropbox>
      )}
    </div>
  );
};

const SearchResult = ({
  matched,
  value,
  children,
  renderOption,
  onSelect,
  setText,
  noMatchMessage,
  renderSearchResult,
  setExpanded
}: P) => {
  const noMatchMsg = (
    <div className="no-match-msg">{noMatchMessage || 'No matches found.'}</div>
  );
  const srOpts: SROpts = {
    children,
    noMatchMessage: noMatchMsg,
    text: value,
    setText,
    hideDropdown: () => {
      if (setExpanded) setExpanded(false);
    }
  };
  if (renderSearchResult) {
    return renderSearchResult(matched, srOpts);
  }
  return (
    <>
      {children}
      <ul className="dd-list">
        {matched?.map((m, i) => (
          <li
            key={i}
            onClick={
              onSelect
                ? () => {
                    onSelect(m);
                    srOpts.hideDropdown();
                  }
                : undefined
            }
          >
            {renderOption(m)}
          </li>
        ))}
        {!matched?.length && noMatchMsg}
      </ul>
    </>
  );
};

const InputGroup = ({
  value,
  disabled,
  placeholder,
  onFocus,
  onBlur,
  handleSearch,
  onRawTextChange,
  inputContainer: IC,
}: P) => {
  const input = (
    <>
      <input
        type="text"
        className="form-control"
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={e => {
          if (onRawTextChange) onRawTextChange(e.target.value);
          handleSearch(e.target.value);
        }}
        onFocus={onFocus}
        onBlur={onBlur}
      />
       <SearchInput/>
    </>
  );
  return (
    <div className="input-group">
      {!!IC && <IC>{input}</IC>}
      {!IC && input}
    </div>
  );
};

type TCProps<T> = Props<T> & {
  selected: T | T[] | ((t: T) => boolean);
};

const getName = (t: any) => {
  if (typeof t == 'string') return t;
  return t.name || t.label || t.props?.name || t;
};

export const TypeaheadChecklist = <T,>({
  selected,
  onSelect,
  ...props
}: TCProps<T>) => {
  const isSelected = React.useCallback(
    (t: T) => {
      const fn = selected as any;
      if (typeof fn == 'function') return fn(t);
      if (Array.isArray(selected)) return selected.includes(t);
      return selected == t;
    },
    [selected]
  );
  return (
    <Typeahead
      className="typeahead-check"
      {...props}
      renderSearchResult={(matched, opts) => {
        if (opts) {
          const { noMatchMessage, text, children } = opts;

          if (!!text && matched?.length === 0) {
            return (
              <>
                {!!children && <div>{children}</div>}
                {noMatchMessage}
              </>
            );
          }
          return (
            <>
              {!!children && <div>{children}</div>}
              <div className="scrollable">
                <ul className="dd-list">
                  {matched.map(o => {
                    const name = getName(o);
                    return (
                      <li
                        className={cn({ 'dd-checked': isSelected(o) })}
                        key={name}
                        onClick={() => {
                          if (onSelect) {
                            onSelect(o, opts);
                            if (!props.inline) {
                              opts.hideDropdown();
                            }
                          }
                        }}
                      >
                        <span>{name}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          );
        }
      }}
      includes={(t, d: any) => {
        const name = getName(d);
        if (!name) return false;
        if (!t) return true;
        const txt = t.trim().toLowerCase();
        return name.toLowerCase().includes(txt);
      }}
    />
  );
};
